import { FC, Fragment, useContext, useEffect, useRef, useState } from 'react';
import { MoContext } from 'contexts/MoContext';
import { actionTargetsOrder, useOrdersAction } from '../Assistant/ordersActions';
import { useTranslation } from 'react-i18next';
import { MyOrder } from 'ts/shared/interfaces/MOResponse';
import { saveDocket } from 'services/myOrders';
import { showError, showSuccess } from 'utils/utils';
import { getStatus } from 'constants/mo';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from 'components/ui/Button/Button';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import useModalClass from 'hooks/useModalClass';
import { faFloppyDisk } from '@fortawesome/free-regular-svg-icons';
import ScreenshotSupport from '../../ScreenshotSupport';
import MaximizeModal from '../../MaximizeModal';
import withErrorBoundary from '../../withErrorBoundary';
import { createPortal } from 'react-dom';
import useDraggableModal from 'hooks/useDraggableModal';
import { MainContext } from 'contexts/MainContext';

interface MoSaveDocketProps {
  onSuccess: (row: MyOrder) => void;
  myOrder: MyOrder;
}

const MoSaveDocket: FC<MoSaveDocketProps> = ({ myOrder, ...props }) => {
  const [t] = useTranslation();
  const modalOverlayRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [isChecked, setIsChecked] = useState(myOrder.order.segments.map(() => true));
  const classList = useModalClass(openModal);
  const { modalRef, modalStyle, dragHandleProps, isDragging } = useDraggableModal(true);
  const { user } = useContext(MainContext);
  const { activeOrder } = useContext(MoContext);
  const isActive =
    activeOrder?.fullSegment.orderSegId === myOrder.fullSegment.orderSegId ||
    activeOrder?.orderRow.segmentId === myOrder.orderRow.segmentId;
  const [type, setType] = useState(1);
  const [docket, setDocket] = useState('');

  useEffect(() => {
    if (openModal) {
      setType(1);
      setDocket('');
    }
  }, [openModal]);

  const onClickSave = async (docketValue = docket, typeValue = type) => {
    setIsLoading(true);
    setOpenModal(false);

    const segments = myOrder.order.segments.filter((_, index) => isChecked[index]);

    if (!segments.length) {
      showError(t('mo.no_segment_selected'));
      setIsLoading(false);
      return;
    }

    segments.forEach(async (segment) => {
      const res = await saveDocket({
        orderId: myOrder.order.orderId,
        segmentId: segment.orderSegId,
        backOfficeId: docketValue,
        useTopBackOfficeId: typeValue === 2,
      });
      if (res && !res?.hasErrors) {
        showSuccess('Docket number successfully changed to: ' + docketValue);
        props.onSuccess({ ...res.myOrder, isSameIdColor: myOrder.isSameIdColor });
      } else if (res?.hasErrors) {
        showError(res.errorMessage);
      }
    });

    setIsLoading(false);
  };

  useOrdersAction(
    (action) =>
      action.action === 'save-docket' &&
      actionTargetsOrder(
        action,
        {
          segmentId: myOrder.orderRow.segmentId,
          altSegmentId: myOrder.fullSegment.orderSegId,
          orderId: myOrder.orderRow.orderId,
          altOrderId: myOrder.fullSegment.orderId,
        },
        isActive,
      ),
    (action) => {
      if (action.docket) {
        if (action.docketType) setType(action.docketType);
        setDocket(action.docket);
        void onClickSave(action.docket, action.docketType || type);
        return;
      }
      setOpenModal(true);
    },
    isActive,
  );

  return (
    <Fragment>
      <Button
        disabled={isLoading}
        onClick={() => setOpenModal(true)}
        title={t(isLoading ? 'mo.loading' : 'Save Docket')}
        icon={faFloppyDisk}
        iconclassname="mr-5"
        className="btn btn-primary btn-ssm"
      />

      {openModal && createPortal(
        <div
          className="modal-overlay modal-overlay-top modal-maximizable" style={{ direction: 'ltr' }}
          ref={modalOverlayRef}
          onClick={(e) => e.target === e.currentTarget && setOpenModal(false)}
        >
          <div ref={modalRef} className={classList.replace('modal-sm', 'modal-st')} style={modalStyle}>
            <div
              className={'modal-title  draggable-modal-handle' + (isDragging ? ' dragging' : '')}
              {...dragHandleProps}
            >
              <h5 className="title">

                Save Docket Number{' '}
                {' '}-{' '}Order: {myOrder.orderRow.orderId}{' '}
                Segment: {myOrder.fullSegment.orderSegId}{' '}
                <span className={`mo-status ${getStatus(myOrder.fullSegment.roomsStatusCode)}`}>
                  {myOrder.fullSegment.roomsStatusCode}
                </span>
              </h5>
              <div data-no-drag="true">
                <ScreenshotSupport />
                <MaximizeModal modalOverlayRef={modalOverlayRef} />
                <FontAwesomeIcon icon={faTimes} onClick={() => setOpenModal(false)} />
              </div>
            </div>

            <div className="modal-body">
              <div className="mb-10">{t('mo.segments')}</div>
              {myOrder.order.segments.map((segment, index) => (
                <Checkbox
                  checked={isChecked[index]}
                  onChange={(e) => {
                    const newChecked = [...isChecked];
                    newChecked[index] = e;
                    setIsChecked(newChecked);
                  }}
                  className="mb-10 flex-v-start text-lh"
                  htmlLabel={
                    <span className="ml-5">
                      <span className="text-bold">[{segment.orderSegId}]</span>
                      <span className="ml-5 mo-status mo-status-secondary">
                        {t('mo.docket')}: {segment.backOfficeId}
                      </span>
                      {segment.itemDesc}
                    </span>
                  }
                />
              ))}

              {user?.useTopBackOfficeId && (
                <div className="mb-20">
                  <div className="mb-5">Type</div>
                  <SelectCustom
                    options={[
                      { label: 'Docket', value: 1 },
                      { label: 'Tour Backoffice', value: 2 },
                    ]}
                    value={type}
                    onChange={setType}
                  />
                </div>
              )}

              <FormGroup value={docket} onChange={setDocket} label={t('mo.docket')} />
            </div>
            <div className="modal-footer">
              <Button
                className="btn btn-primary ml-0 mr-a"
                title={t('save')}
                onClick={() => onClickSave()}
              />
              <Button
                title={t('cancel')}
                className="btn btn-default"
                onClick={() => setOpenModal(false)}
              />
            </div>
          </div>
        </div>
        , document.body)}
    </Fragment>
  );
};

export default withErrorBoundary(MoSaveDocket, 'MoSaveDocket');
